import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { SwUpdate } from '@angular/service-worker';
import {
  ApiService,
  ExportService,
  ExportWebSocketService,
  GlobalModalsService,
  IdentityService, JwtService, LocalStorageService, NotificationService,
  TitleService, UserSettingsService
} from './core/services';
import { FlourService } from './pages/bulk-containers/core/services/FlourService';
import * as dayjs from 'dayjs';
import 'dayjs/locale/de';
import { IClaim } from './core/interfaces/auth/IClaim';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { SearchModalComponent } from './ui/modals/search-modal/search-modal.component';
import { DocumentCodes } from './core/enums';
import { StoragePlaceFinderModalComponent } from './pages/stores/storage-place-finder-modal/storage-place-finder-modal.component';
import { DeliveryNoteProcessModalComponent } from './pages/orders/delivery-note-process-modal/delivery-note-process-modal.component';
import { ProcessModalComponent } from './pages/production-orders/process-modal/process-modal.component';
import { AddEditOrderModalComponent } from './pages/bulk-containers/orders/add-edit-order-modal/add-edit-order-modal.component';
import { OrdersComponent } from './pages/bulk-containers/orders/orders.component';
import { filter } from 'rxjs/operators';
import { StockTransferComponent } from './pages/stock-transfer/stock-transfer/stock-transfer.component';
import { StockTransferProcessModalComponent } from './pages/stock-transfer/stock-transfer-process-modal/stock-transfer-process-modal.component';
import { environment } from '../environments/environment';
import { formatAppVersion } from './core/utils/app-version';
import { MenuItem } from './core/models/menu-item.model';
import { WelcomeComponent } from './pages/welcome/welcome/welcome.component';
import { IFlourPackingListItem } from './pages/flour/core/interfaces';
import { ExportsPageComponent } from './pages/tools/pages/exports-page/exports-page.component';
import { ScannerService } from './core/services/ScannerService';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UNKNOWN_BARCODE_MESSAGE } from './core/utils';
import { StoresService } from './pages/stores/core/services/StoresService';
import { ISimpleListItem } from './core/interfaces/ISimpleListItem';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {

  @ViewChild('searchModalComponent') searchModalComponent: SearchModalComponent;
  @ViewChild('storagePlaceFinderModalComponent') storagePlaceFinderModalComponent: StoragePlaceFinderModalComponent;
  @ViewChild('deliveryNoteProcessModalComponent') deliveryNoteProcessModalComponent: DeliveryNoteProcessModalComponent;
  @ViewChild('processModalComponent') processModalComponent: ProcessModalComponent;
  @ViewChild('addEditOrderModalComponent') addEditOrderModalComponent: AddEditOrderModalComponent;
  @ViewChild('stockTransferProcessModal') stockTransferProcessModal: StockTransferProcessModalComponent;

  public isCollapsed = false;
  public menuItems: MenuItem[] = environment.menuItems;
  public currentUser: IClaim;
  public activatedComponent: any;
  public version = '';
  private _ctrlOrCommandKeyClicked = false;
  private _enterKeyClicked = false;
  private readonly _md = 768;
  private _scannedVal = '';
  private _timer: ReturnType<typeof setTimeout>;
  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    private _swUpdate: SwUpdate,
    private _identityService: IdentityService,
    private _jwtService: JwtService,
    private readonly _titleService: TitleService,
    private _globalModalService: GlobalModalsService,
    private _exportWebSocketService: ExportWebSocketService,
    private _exportService: ExportService,
    private _notificationService: NotificationService,
    private _router: Router,
    private _localStorafeSrvice: LocalStorageService,
    private _cdr: ChangeDetectorRef,
    private _flourService: FlourService<IFlourPackingListItem>,
    private _apiService: ApiService,
    private _scannerService: ScannerService,
    _userSettings: UserSettingsService,
    private _storesService: StoresService,
  ) {
  }

  @HostListener('window:resize')
  @HostListener('window:load')
  public onResize(): void {
    this.isCollapsed = window.innerWidth < this._md || !!parseInt(localStorage.getItem('isSiderOpened'), 10);
  }

  ngOnInit(): void {
    this.isCollapsed = !!parseInt(localStorage.getItem('isSiderOpened'), 10);
    registerLocaleData(localeDe);
    registerLocaleData(localeDe, 'de-DE');
    dayjs.locale('de');
    this._identityService.populate();
    this._jwtService.clearWorkstationMode();
    this._titleService.populate();
    if (this._swUpdate.isEnabled) {
      this._swUpdate.versionUpdates.pipe(
        filter(event => event.type === 'VERSION_READY')
      ).subscribe( () => this.showNewVersion());
    }
    this._apiService.getVersion().subscribe((version) => {
      this.version = formatAppVersion(environment.version, version.active);
      if (version.prev !== null && version.prev !== '') {
        this.showNewVersion();
      }
    });
    this.initExportWebSocket();
    this._identityService.isAuthenticated.subscribe(this.setAppDetails.bind(this));
    this._storesService.changed$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.currentUser) {
        this.loadFlourStoreMenu();
      }
    });

    this._router.events.pipe(
      filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(evt => {
      this.syncOpenSubmenu(evt.urlAfterRedirects);
      this.syncMenuModalsWithUrl(evt.urlAfterRedirects);
    });
    this.syncOpenSubmenu(this._router.url);

    if ('ontouchstart' in window) {
      fromEvent<KeyboardEvent>(document, 'keydown').pipe(
        takeUntilDestroyed(this._destroyRef)
      ).subscribe(e => {
        const textInput = e.key || String.fromCharCode(e.keyCode);
        const targetName = e.target['localName'];

        if (textInput.length === 1 && targetName !== 'input') {
          this._scannedVal += textInput;

          if (this._timer) {
            clearTimeout(this._timer);
          }

          this._timer = setTimeout(() => {
            if (this._scannerService.isMainScannerEnabled) {
              this.searchChanged(this._scannedVal);
            } else {
              this._scannerService.scannedVal$.next(this._scannedVal);
            }

            this._scannedVal = '';
            this._cdr.detectChanges();
          }, 100);
        }

      });
    }
  }

  openModal(key: string, param: any = null): void {
    this._globalModalService.openModal(key, param);
  }

  public onSubmenuOpenChange(index: number, open: boolean): void {
    if (open) {
      this.menuItems.forEach((item, i) => item.isOpen = i === index);
      return;
    }
    this.menuItems[index].isOpen = false;
  }

  public onSubmenuTitleClick(event: MouseEvent, index: number): void {
    if ((event.target as HTMLElement).closest('.ant-menu-item')) {
      return;
    }
    this.onSubmenuOpenChange(index, true);
  }

  public onCollapsedChange(collapsed: boolean): void {
    this.isCollapsed = collapsed;
    localStorage.setItem('isSiderOpened', collapsed ? '1' : '0');
  }

  public toggleSidebar(): void {
    this.onCollapsedChange(!this.isCollapsed);
  }

  public refreshOrdersList(): void {
    if (this.activatedComponent instanceof OrdersComponent) {
      this.activatedComponent.listComponent.onRefreshList();
    }
  }

  public searchChanged(val: string): void {
    const prefix = val.slice(0, 2);
    if (prefix === DocumentCodes.ORDER) {
      this.addEditOrderModalComponent.showModal({ id: parseInt(val.slice(2), 10) } as any);
    } else if (prefix === DocumentCodes.PRODUCTION_ORDER) {
      this.openModal('productionOrderProcessModal');
      this.processModalComponent.orderNumber = val.slice(2);
      setTimeout(() => this.processModalComponent.handleNumberScan(val.slice(2)));
    } else if (prefix === DocumentCodes.DELIVERY_NOTE) {
      this.openModal('deliveryNoteProcessModal');
      this.deliveryNoteProcessModalComponent.orderNumber = val;
      this.deliveryNoteProcessModalComponent.handleNumberScan(val);
    } else if (val.length === 13) {
      this.openModal('storagePlaceFinderModal');
      this.storagePlaceFinderModalComponent.eanCode = val;
      this.storagePlaceFinderModalComponent.handleNumberScan();
    } else if (prefix === DocumentCodes.STOCK_TRANSFER) {
      this._flourService.getPackingListById(+val.slice(2)).subscribe(res => {
        setTimeout(() => {
          const type = res.isOutgoingDoc ? 1 : 0;
          this.openModal('stockTransferProcessModal', { type });
          this.stockTransferProcessModal.transferNumber = val.slice(2);
          setTimeout(() => this.stockTransferProcessModal.handleNumberScan(val.slice(2) + '\n'));
          this.searchModalComponent.closeModal();
        });
      }, () => this._notificationService.pushError('Fehler', 'Fehler beim Abrufen der Packliste.'));
    } else {
      this._notificationService.pushError('Fehler', UNKNOWN_BARCODE_MESSAGE);
      return;
    }

    if (prefix !== DocumentCodes.STOCK_TRANSFER) {
      this.searchModalComponent.closeModal();
    }
  }

  private initExportWebSocket(): void {
    this._identityService.isAuthenticated.pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(isAuth => {
      if (!isAuth) {
        this._exportWebSocketService.disconnect().subscribe({ error: () => {} });
        return;
      }
      this._exportWebSocketService.start().subscribe({
        error: err => console.error('Export WebSocket failed to start', err)
      });
    });
    this._exportWebSocketService.exportDocumentGenerated(({ operationId, response, hasError }) => {
      if (this._exportService.operationIdExists(operationId)) {
        if (!hasError) {
          this._exportService.exportGenerated = response;
          this._exportService.removeOperationId(operationId);
          this._notificationService.pushSuccess('Erfolg', `Das Exportdokument kann heruntergeladen werden. <a href="/tools/exports">Zu Exporten gehen</a>`);

          if (this.activatedComponent instanceof ExportsPageComponent) {
            this.activatedComponent.exportListViewComponentRef.onRefreshList(false);
          }
        } else {
          this._notificationService.pushError('Fehler', 'Beim Generieren des Exportdokuments ist ein Fehler aufgetreten');
        }
      }
    });
  }

  setAppDetails(val?: boolean) {
    this.getUserDetails(val || null);
    if (this.currentUser) {
      this.loadFlourStoreMenu();
    } else {
      this.clearFlourStoreMenu();
    }
  }

  private loadFlourStoreMenu(): void {
    this._storesService.getSimple().subscribe(stores => this.applyFlourStoreMenu(stores));
  }

  private applyFlourStoreMenu(stores: ISimpleListItem[]): void {
    const group = this.menuItems.find(item => item.name === 'Lagerhäuser' && item.elements);
    if (!group?.elements) {
      return;
    }

    const staticItems = group.elements.filter(item => !item.dynamicFlour);
    const flourItems: MenuItem[] = stores
      .filter(store => store.additionalParameters?.isFlourStore === 'True')
      .map(store => ({
        name: `Warentransfer ${store.additionalParameters?.shortCode || store.value}`,
        routerLink: `/flour/store/${store.id}`,
        isMobile: false,
        exact: true,
        dynamicFlour: true,
      }));
    const anchor = staticItems.findIndex(item => item.routerLink === '/stock-transfer');
    const index = anchor < 0 ? 0 : anchor;
    group.elements = [
      ...staticItems.slice(0, index),
      ...flourItems,
      ...staticItems.slice(index),
    ];
    this._cdr.markForCheck();
  }

  private clearFlourStoreMenu(): void {
    const group = this.menuItems.find(item => item.name === 'Lagerhäuser' && item.elements);
    if (!group?.elements) {
      return;
    }
    group.elements = group.elements.filter(item => !item.dynamicFlour);
  }

  getUserDetails(val?: boolean) {
    this.currentUser = val ? this._jwtService.getIdentity().claims : null;
    if (this.currentUser) {
      this._localStorafeSrvice.setLocalStorageItemKey(this.currentUser.userID);
    }
  }

  public get isDashboard(): boolean {
    return this.activatedComponent instanceof WelcomeComponent;
  }

  showNewVersion() {
    if (confirm('Neue Version verfügbar. Neue Version laden?')) {
      window.location.reload();
    }
  }

  public refreshStockTransfersList(): void {
    if (this.activatedComponent instanceof StockTransferComponent) {
      this.activatedComponent.listComponent.onRefreshList();
    }
  }

  private syncOpenSubmenu(url: string): void {
    const path = url.split('?')[0];
    const parent = this.menuItems.find(item =>
      item.elements?.some(child => this.menuLinkMatches(path, child))
    );
    this.menuItems.forEach(item => {
      item.isOpen = !!parent && item === parent;
    });
    this._cdr.markForCheck();
    this.scrollActiveMenuItemIntoView();
  }

  private menuLinkMatches(path: string, item: MenuItem): boolean {
    const link = item.routerLink ?? '';
    if (link === '' || link === '/') {
      return path === '/' || path === '';
    }
    if (path === link) {
      return true;
    }
    if (item.exact) {
      return false;
    }
    return path.startsWith(`${link}/`);
  }

  private scrollActiveMenuItemIntoView(): void {
    if (this.isCollapsed) {
      return;
    }
    setTimeout(() => {
      const menu = document.querySelector<HTMLElement>('.menu');
      const selected = document.querySelector<HTMLElement>('.menu .ant-menu-item-selected');
      if (!menu || !selected) {
        return;
      }
      menu.querySelectorAll<HTMLElement>('.ant-menu-sub').forEach(sub => {
        sub.scrollTop = 0;
      });
      const menuRect = menu.getBoundingClientRect();
      const itemRect = selected.getBoundingClientRect();
      if (itemRect.top >= menuRect.top && itemRect.bottom <= menuRect.bottom) {
        return;
      }
      menu.scrollTop += itemRect.top - menuRect.top - (menu.clientHeight - itemRect.height) / 2;
    }, 50);
  }

  private syncMenuModalsWithUrl(url: string): void {
    const path = url.split('?')[0];
    const match = this.findMenuModalItem(path);
    const modalRefs = this.collectMenuModalRefs();

    for (const ref of modalRefs) {
      if (ref !== match?.modalRef) {
        this._globalModalService.closeModal(ref);
      }
    }

    if (match?.modalRef) {
      this.openModal(match.modalRef, match.modalParam);
    }
  }

  private findMenuModalItem(path: string): MenuItem | undefined {
    const walk = (items: MenuItem[]): MenuItem | undefined => {
      for (const item of items) {
        if (item.showModal && item.routerLink === path) {
          return item;
        }
        if (item.elements) {
          const nested = walk(item.elements);
          if (nested) {
            return nested;
          }
        }
      }
      return undefined;
    };
    return walk(this.menuItems);
  }

  private collectMenuModalRefs(): string[] {
    const refs = new Set<string>();
    const walk = (items: MenuItem[]): void => {
      for (const item of items) {
        if (item.showModal && item.modalRef) {
          refs.add(item.modalRef);
        }
        if (item.elements) {
          walk(item.elements);
        }
      }
    };
    walk(this.menuItems);
    return [...refs];
  }
}
